// Miky Motorsport - VLASTNI slip-based vehicle dynamics (M1b)
// Chassis = Rapier dynamic rigid body, kola = 4 vlastni raycasty.
// Per tick: raycast suspension -> normalova sila N (addForceAtPoint),
//   slip ratio/angle -> Pacejka long/lat -> friction ellipse -> tire force,
//   wheel omega dynamika (engine torque - tire reaction - brzda),
//   engine + prevodovka + diferencial, SAT yaw torque.
// Poradi operaci dle planu v3. Determinismus neni potreba (ghosti = trajektorie).
import * as THREE from 'three';
import { RAPIER } from './world.js';
import { DEG_TO_RAD, MS_TO_KMH } from '../constants.js';
import {
  magicFormula, slipRatio, slipAngle, frictionEllipse,
  engineTorque, wheelOmegaToRpm, absFactor, clamp,
} from './tires.js';
import { yawQuat, brakeOmega, steerAngleFor, antiRollTorque } from '../vehicleMath.js';

const V_FLOOR = 2.5; // m/s - zabranuje divergenci slip ratio u nizke rychlosti
const V_FLOOR_LAT = 2.0;
const OMEGA_MAX = 600; // rad/s - strop uhlove rychlosti kola (anti-divergence)
// Vyska "roll center" (m) nad kontaktnim bodem, kde se aplikuje PRICNA sila pneumatiky.
// Bez tohoto (=0) se fLat aplikuje u zeme a paka klopneho momentu = cela vyska teziste -> grip
// silnejsich/upgradovanych aut prekroci geometricky prah prevraceni a auto se naklani na 2
// kola (a cim vic upgradu/gripu, tim hur). Zvednutim pusobiste se paka zkrati -> auto zacne
// KLOUZAT driv, nez se prevrati. 0.40 drzi planted i nejgripovejsi upgradovane auto (muLat 2.0),
// pricemz grip v rovine (friction ellipse) zustava netknuty. Yaw je take netknuty (zvednuti
// pusobiste meni jen roll/pitch coupling, ne vodorovnou paku).
const ROLL_CENTER_H = 0.40;

export class Vehicle {
  constructor(world, spec, spawn = { x: 0, y: 1.05, z: 0 }, headingRad = 0) {
    this.world = world;
    this.spec = spec;
    this.drivenIdx = spec.drive === 'RWD' ? [2, 3] : spec.drive === 'AWD' ? [0, 1, 2, 3] : [0, 1];
    this.frontWheels = [0, 1];
    this.rearWheels = [2, 3];

    // --- Chassis rigid body ---
    const c = spec.chassis;
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(spawn.x, spawn.y, spawn.z)
      .setRotation(yawQuat(headingRad))
      .setCcdEnabled(true)
      .setLinearDamping(0.04)
      .setAngularDamping(0.25)
      .setCanSleep(false);
    this.body = world.createRigidBody(bodyDesc);
    const colDesc = RAPIER.ColliderDesc.cuboid(c.hx, c.hy, c.hz)
      .setMass(c.mass)
      .setFriction(0.2)
      .setRestitution(0.0) // zadny odraz pri narazu (auta se neodmrsti)
      .setTranslation(0, c.comY, 0);
    this.collider = world.createCollider(colDesc, this.body);

    // --- Kola ---
    this.wheels = spec.wheel.positions.map((p, i) => ({
      connLocal: new THREE.Vector3(p.x, p.y, p.z),
      omega: 0,
      suspLength: spec.suspension.restLength,
      prevCompression: 0,
      steer: 0,
      roll: 0,
      load: 0,
      contact: false,
      slipRatio: 0,
      slipAngle: 0,
      driven: this.drivenIdx.includes(i),
      front: i < 2,
    }));

    this.gear = 0;
    this.engineRpm = spec.engine.idleRpm;
    this.surfaceGrip = 1; // 1 = asfalt; <1 mimo trat (trava/pisek) - nastavuje RaceManager
    this.assists = { abs: true, tcs: true, stability: true }; // asistenti (default ON)
    this.controls = { throttle: 0, brake: 0, steer: 0, handbrake: false };
    this.maxSteerRad = spec.maxSteerDeg * DEG_TO_RAD;
    this._speedMs = 0;
    this._contacts = 0;
    this._shiftTimer = 0;
    this._settle = 14;

    // Reusable temporaries (bez alokaci v loopu)
    this._q = new THREE.Quaternion();
    this._pos = new THREE.Vector3();
    this._com = new THREE.Vector3();
    this._fwd = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._up = new THREE.Vector3();
    this._down = new THREE.Vector3();
    this._conn = new THREE.Vector3();
    this._contact = new THREE.Vector3();
    this._r = new THREE.Vector3();
    this._linvel = new THREE.Vector3();
    this._angvel = new THREE.Vector3();
    this._vC = new THREE.Vector3();
    this._wFwd = new THREE.Vector3();
    this._wLat = new THREE.Vector3();
    this._force = new THREE.Vector3();
    this._rollPt = new THREE.Vector3();
    this._sat = new THREE.Vector3();
    this._ray = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: -1, z: 0 });
  }

  applyInput(controls) {
    this.controls = controls;
  }

  update(dt) {
    const body = this.body;
    body.resetForces(false);
    body.resetTorques(false);

    const spec = this.spec;
    const susp = spec.suspension;
    const tire = spec.tire;
    const brakes = spec.brakes;
    const radius = spec.wheel.radius;
    const inertia = spec.wheel.inertia;

    const r = body.rotation();
    this._q.set(r.x, r.y, r.z, r.w);
    const t = body.translation();
    this._pos.set(t.x, t.y, t.z);
    const com = body.worldCom();
    this._com.set(com.x, com.y, com.z);
    const lv = body.linvel();
    this._linvel.set(lv.x, lv.y, lv.z);
    const av = body.angvel();
    this._angvel.set(av.x, av.y, av.z);

    this._fwd.set(0, 0, 1).applyQuaternion(this._q);
    this._right.set(1, 0, 0).applyQuaternion(this._q);
    this._up.set(0, 1, 0).applyQuaternion(this._q);
    this._down.copy(this._up).multiplyScalar(-1);

    this._speedMs = this._linvel.dot(this._fwd);
    const speedKmh = Math.abs(this._speedMs) * MS_TO_KMH;

    // --- Rizeni (speed-sensitive, negace: vpravo -> vpravo) ---
    const steerAngle = steerAngleFor(this.controls.steer, speedKmh, this.maxSteerRad);
    for (const i of this.frontWheels) this.wheels[i].steer = steerAngle;

    // --- Engine / prevodovka / diferencial ---
    const dr = spec.drivetrain;
    let drivenOmega = 0;
    for (const i of this.drivenIdx) drivenOmega += this.wheels[i].omega;
    drivenOmega /= this.drivenIdx.length;

    const reverse = this._speedMs < 0.6 && this.controls.brake > 0.05 && this.controls.throttle < 0.05;
    let driveTorquePerWheel = 0;
    if (reverse) {
      const cap = this._speedMs > -6.1; // omez reverz na ~22 km/h
      const eT = cap ? engineTorque(spec.engine.torqueCurve, spec.engine.idleRpm + 1500) * 0.7 : 0;
      driveTorquePerWheel = -(eT * dr.reverseRatio * dr.finalDrive * dr.efficiency) / this.drivenIdx.length;
      this.engineRpm = spec.engine.idleRpm;
    } else {
      const gearRatio = dr.gears[this.gear];
      let rpm = wheelOmegaToRpm(drivenOmega, gearRatio, dr.finalDrive);
      rpm = clamp(rpm, spec.engine.idleRpm, spec.engine.redlineRpm);
      this.engineRpm = rpm;
      this._shiftTimer -= dt;
      if (this._shiftTimer <= 0) {
        if (rpm > dr.shiftUpRpm && this.gear < dr.gears.length - 1) { this.gear++; this._shiftTimer = 0.4; }
        else if (rpm < dr.shiftDownRpm && this.gear > 0) { this.gear--; this._shiftTimer = 0.4; }
      }
      const eT = engineTorque(spec.engine.torqueCurve, rpm) * clamp(this.controls.throttle, 0, 1);
      driveTorquePerWheel = (eT * gearRatio * dr.finalDrive * dr.efficiency) / this.drivenIdx.length;
    }
    const brakeInput = reverse ? 0 : clamp(this.controls.brake, 0, 1);

    // --- Per-wheel fyzika ---
    this._sat.set(0, 0, 0);
    let contacts = 0;
    const maxToi = susp.restLength + radius + susp.margin;

    for (let i = 0; i < this.wheels.length; i++) {
      const wh = this.wheels[i];
      this._conn.copy(wh.connLocal).applyQuaternion(this._q).add(this._pos);
      this._ray.origin = { x: this._conn.x, y: this._conn.y, z: this._conn.z };
      this._ray.dir = { x: this._down.x, y: this._down.y, z: this._down.z };
      const hit = this.world.castRayAndGetNormal(this._ray, maxToi, true, undefined, undefined, undefined, this.body);

      const driveT = wh.driven ? driveTorquePerWheel : 0;

      if (hit) {
        contacts++;
        const d = hit.timeOfImpact;
        const suspLen = clamp(d - radius, 0, susp.restLength);
        const compression = susp.restLength - suspLen;
        // [F-501] Prvni kontaktni tick po letu: prevCompression je 0, takze
        // compVel = compression/dt by byl obrovsky -> phantom N spike (a tim grip spike)
        // pri kazdem dopadu/kerbu. Seed prevCompression = compression -> compVel = 0,
        // zadny falesny damping. Bezne spojite tlumeni (kolo bylo v kontaktu) nezmeneno.
        if (!wh.contact) wh.prevCompression = compression;
        const compVel = (compression - wh.prevCompression) / dt;
        let N = susp.stiffness * compression + susp.damping * compVel;
        N = clamp(N, 0, susp.maxForce);
        wh.prevCompression = compression;
        wh.load = N;
        wh.contact = true;
        wh.suspLength = suspLen;

        // suspension sila (podel chassis up) v connection pointu
        this._force.copy(this._up).multiplyScalar(N);
        body.addForceAtPoint(this._force, this._conn, true);

        // kontaktni bod a rychlost chassis v nem
        this._contact.copy(this._down).multiplyScalar(d).add(this._conn);
        this._r.copy(this._contact).sub(this._com);
        this._vC.copy(this._angvel).cross(this._r).add(this._linvel);

        // smer kola (rejd)
        this._wFwd.copy(this._fwd);
        this._wLat.copy(this._right);
        if (wh.front && wh.steer !== 0) {
          this._wFwd.applyAxisAngle(this._up, wh.steer);
          this._wLat.applyAxisAngle(this._up, wh.steer);
        }
        const vLong = this._vC.dot(this._wFwd);
        const vLat = this._vC.dot(this._wLat);

        const maxLong = tire.muLong * N * this.surfaceGrip;
        const maxLat = tire.muLat * N * this.surfaceGrip;

        // --- micro-step tuhe podelne wheel dynamiky (stabilita stiff ODE) ---
        const MICRO = 24;
        const mdt = dt / MICRO;
        let fLongSum = 0;
        let sr = 0;
        for (let m = 0; m < MICRO; m++) {
          sr = clamp(slipRatio(wh.omega, radius, vLong, V_FLOOR), -6, 6);
          const fL = magicFormula(sr, tire.Blong, tire.Clong, maxLong, tire.E);
          // TCS: omez hnaci moment pri prokluzu
          const driveEff = this.assists.tcs && driveT > 0 && sr > 0.2 ? driveT * 0.15 : driveT;
          wh.omega += ((driveEff - fL * radius) / inertia) * mdt;
          let bt = brakeInput * brakes.maxTorque * (wh.front ? 1.12 : 0.88);
          if (this.assists.abs) bt *= absFactor(sr); // ABS
          if (this.controls.handbrake && !wh.front) bt += brakes.handbrakeTorque;
          const bd = (bt / inertia) * mdt;
          wh.omega = brakeOmega(wh.omega, bd);
          // tvrdy strop omega (anti-divergence pro silnejsi budouci auta)
          if (wh.omega > OMEGA_MAX) wh.omega = OMEGA_MAX;
          else if (wh.omega < -OMEGA_MAX) wh.omega = -OMEGA_MAX;
          fLongSum += fL;
        }
        let fLong = fLongSum / MICRO;
        wh.slipRatio = sr;

        // --- pricna sila (Pacejka lat) ---
        const sa = slipAngle(vLat, vLong, V_FLOOR_LAT);
        wh.slipAngle = sa;
        let fLat = magicFormula(sa, tire.Blat, tire.Clat, maxLat, tire.E);

        // friction ellipse (kombinovany slip)
        const fe = frictionEllipse(fLong, fLat, maxLong, maxLat);
        fLong = fe.fLong;
        fLat = fe.fLat;

        // Podelna sila pneumatiky v kontaktnim bode (zachova pitch/dive/squat feel).
        this._force.copy(this._wFwd).multiplyScalar(fLong);
        body.addForceAtPoint(this._force, this._contact, true);
        // Pricna sila ve vysce roll center (contact + up*ROLL_CENTER_H) -> kratsi paka
        // klopneho momentu, auto klouze drive nez se prevrati. Yaw (vodorovna slozka paky)
        // zustava netknuty - zvednuti meni jen roll/pitch coupling.
        this._force.copy(this._wLat).multiplyScalar(fLat);
        this._rollPt.copy(this._up).multiplyScalar(ROLL_CENTER_H).add(this._contact);
        body.addForceAtPoint(this._force, this._rollPt, true);

        // self-aligning torque (steering feel)
        const Mz = -tire.satCoef * Math.sin(sa) * N * tire.pneumaticTrail;
        this._sat.addScaledVector(this._up, Mz);
      } else {
        // kolo ve vzduchu
        wh.contact = false;
        wh.load = 0;
        wh.prevCompression = 0;
        wh.suspLength = susp.restLength;
        wh.slipRatio = 0;
        wh.slipAngle = 0;
        wh.omega += (driveT / inertia) * dt;
        const bd = (brakeInput * brakes.maxTorque / inertia) * dt;
        wh.omega = brakeOmega(wh.omega, bd);
        wh.omega *= 0.995;
      }
      wh.roll += wh.omega * dt;
    }

    // Stability control: tlumi pretacivost (smyk zadku)
    if (this.assists.stability && contacts > 0) {
      const rearSlide = Math.max(Math.abs(this.wheels[2].slipAngle), Math.abs(this.wheels[3].slipAngle));
      if (rearSlide > 0.22) this._sat.y += -this._angvel.y * body.mass() * 1.4 * (rearSlide - 0.22);
    }

    // --- Anti-roll / self-righting: drzi auto na kolech (proti prevraceni pri narazu) ---
    // Torque srovnavajici lokalni up s world-up (osa = up_car x world_up = (-up.z, 0, up.x),
    // velikost = sin(naklonu)). Silnejsi pri velkem naklonu (anti-flip), jemny kdyz skoro rovne.
    // Tlumi jen roll/pitch (slozky angvel mimo svislou osu), yaw (zataceni) nechava.
    {
      const m = body.mass();
      const ar = antiRollTorque(this._up, this._angvel, m);
      this._sat.x += ar.x;
      this._sat.z += ar.z;
      // u plne inverze (~180 st) je restoring osa nulova (sin=0, nestabilni rovnovaha)
      // -> pridej maly roll torque kolem podelne osy, ktery auto z teto rovnovahy shodi.
      const tilt = 1 - clamp(this._up.y, -1, 1);   // 0 (rovne) .. 2 (vzhuru nohama)
      if (tilt > 1.6) this._sat.addScaledVector(this._fwd, m * 2.6);
    }

    body.addTorque(this._sat, true);

    // Mimo trat: valivy odpor (trava/pisek zpomaluje)
    if (this.surfaceGrip < 0.75 && contacts > 0) {
      const k = (0.75 - this.surfaceGrip) * 1.3 * body.mass();
      this._force.set(-this._linvel.x * k, 0, -this._linvel.z * k);
      body.addForce(this._force, true);
    }

    this._contacts = contacts;

    // settle (proti spawn lurchi)
    if (this._settle > 0) {
      this._settle--;
      body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    }
  }

  getState() {
    const t = this.body.translation();
    const r = this.body.rotation();
    const wheels = this.wheels.map((wh) => ({
      cx: wh.connLocal.x, cy: wh.connLocal.y, cz: wh.connLocal.z,
      susp: wh.suspLength, steer: wh.steer, roll: wh.roll, radius: this.spec.wheel.radius,
    }));
    return {
      pos: { x: t.x, y: t.y, z: t.z },
      rot: { x: r.x, y: r.y, z: r.z, w: r.w },
      wheels,
    };
  }

  get speedKmh() {
    return this._speedMs * MS_TO_KMH;
  }
  get speedMs() {
    return this._speedMs;
  }

  // pro castice (smyk): max kombinovany slip na zadnich kolech (0..~1)
  rearSlipAmount() {
    let s = 0;
    for (const i of this.rearWheels) {
      const wh = this.wheels[i];
      s = Math.max(s, Math.abs(wh.slipRatio) * 0.35 + Math.abs(wh.slipAngle) * 1.6);
    }
    return s;
  }

  wheelsInContact() {
    return this._contacts;
  }

  reset(pos, headingRad = 0) {
    this.body.setTranslation({ x: pos.x, y: pos.y, z: pos.z }, true);
    this.body.setRotation(yawQuat(headingRad), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    for (const wh of this.wheels) {
      wh.omega = 0;
      wh.prevCompression = 0;
      wh.roll = 0;
      wh.slipRatio = 0;
      wh.slipAngle = 0;
    }
    this.gear = 0;
    this._settle = 14;
  }

  // Uvolneni fyziky: odstrani rigid body (vc. jeho colliderru) ze sveta.
  // Bez tohoto by kazde auto zustalo v simulaci a zatezovalo CPU/pamet kazdy world.step().
  dispose() {
    if (this.body) {
      this.world.removeRigidBody(this.body);
      this.body = null;
      this.collider = null;
    }
  }
}
